import * as am4core from '@amcharts/amcharts4/core';
import { CategoryAxisDataItem, Column, ColumnSeries, Legend } from '@amcharts/amcharts4/charts';

import { BarChartModel } from './bar-chart.model';
import { DataPointModel } from '../data-point.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { CategoryValueXYChartOptions } from './category-value-x-y-chart-options.model';
import { DateAndValueXYChartUnitEnum } from '../../../Charts/enums/date-and-value-x-y-chart-unit.enum';
import { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { abbreviateNumber } from '../../../Misc/helpers/formatting/number.helper';
import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ChartSizeHelper } from '../../helpers/chart-size.helper';


export class IconBarChartModel extends BarChartModel {
    chartDisplayLimit = 6;
    smallChartDisplayLimit = 3;
    dataIds: string[];

    previousPeriodAmSeries: ColumnSeries;
    previousPeriodColumnTemplate: Column;
    colorList:  Map<string, string> =  new Map<string, string>();

    public initChart(element: HTMLDivElement): void {
        super.initChart(element);

        this.valueAxis.renderer.grid.template.disabled = false;
        this.valueAxis.renderer.labels.template.disabled = false;
        this.valueAxis.renderer.line.strokeOpacity = 1;
        this.valueAxis.renderer.line.strokeWidth = 2;
        this.valueAxis.renderer.line.stroke = am4core.color(chartColors.agprGreyBorder);
        this.valueAxis.min = 0;
        this.valueAxis.paddingRight = 15;

        if (!this.options.hidePreviousPeriod) {
            this.valueAxis.paddingRight = 35;
            this.valueAxis.strictMinMax = true;
            this.valueAxis.extraMax = 0.3; // To prevent the chart from cutting off when it has a higher value
        }

        this.valueAxis.calculateTotals = true;

        if (this.options.xValueLabel) {
            this.valueAxis.title.text = this.options.xValueLabel;
        }

        this.valueAxis.numberFormatter.bigNumberPrefixes = [
            { 'number': 1e+3, 'suffix': 'K' },
            { 'number': 1e+6, 'suffix': 'M' },
            { 'number': 1e+9, 'suffix': 'B' },
            { 'number': 1e+12, 'suffix': 'T' },
            { 'number': 1e+15, 'suffix': 'Q' }
        ];

        if (this.options.unit === DateAndValueXYChartUnitEnum.PERCENTAGE
            || (!this.options.unit && this.options.percentLabel)) {
            this.valueAxis.numberFormatter.numberFormat = '#.#%';
            this.valueAxis.numberFormatter.smallNumberThreshold = 0;
        } else if (this.options.unit === DateAndValueXYChartUnitEnum.CURRENCY) {
            const reportModel = this.series[0] as ReportSeriesModel;
            if (reportModel.styles.currencySymbol){
                this.valueAxis.numberFormatter.numberFormat = `${reportModel.styles.currencySymbol}#,###.#a`;
            }else if (reportModel.styles.currency){
                this.valueAxis.numberFormatter.numberFormat = `#,###.#a ${reportModel.styles.currency}`;
            }else{
                this.valueAxis.numberFormatter.numberFormat = '#,###.#a';
            }
        } else {
            this.valueAxis.numberFormatter.numberFormat = '#.#a';
        }

        // Calculate column height and set the column label height based on it in order to show the label above the column
        this.categoryAxis.events.on('sizechanged', function(ev) {
            const axis = ev.target;
            const cellHeight = axis.pixelHeight / (axis.endIndex - axis.startIndex);

            if (Number.isFinite(cellHeight)) {
                axis.renderer.labels.template.dy = - (cellHeight * 0.6 / 2) - 2;
            }
        });

        this.columnTemplate.height = am4core.percent(60);
        this.labelTemplate.dy = 0;

        this.previousPeriodAmSeries = new ColumnSeries();

        // Sometimes users may add source with identical label names. Thus, here we use id as it is unique
        this.categoryAxis.dataFields.category = 'id';
        this.amseries.dataFields.categoryY = 'id';
        // Later we use labelTemplate.adapter to replace the unique id with label names
        this.labelTemplate.adapter.add('text', (value, target) => {
            return (target as any)?._dataItem?._dataContext?.label;
        });

        this.previousPeriodAmSeries.dataFields.valueX = 'xValuePreviousPeriod';
        this.previousPeriodAmSeries.dataFields.categoryY = 'id';
        this.previousPeriodAmSeries.clustered = false;

        this.previousPeriodColumnTemplate = this.previousPeriodAmSeries.columns.template;
        this.previousPeriodColumnTemplate.height = am4core.percent(80);

        // Previous Period Stroke
        this.previousPeriodColumnTemplate.strokeWidth = 1;
        this.previousPeriodColumnTemplate.strokeOpacity = 1;
        this.previousPeriodColumnTemplate.strokeDasharray = '3,2';
        this.previousPeriodColumnTemplate.stroke = am4core.color(this.styles.colorMap?.['barColor']);

        // set tooltip style
        this.previousPeriodAmSeries.tooltip.getFillFromObject = false;
        this.previousPeriodAmSeries.tooltip.background.fill = am4core.color(chartColors.agprWhite);
        this.previousPeriodAmSeries.tooltip.background.stroke = am4core.color('#ced4da');
        this.previousPeriodAmSeries.tooltip.background.cornerRadius = 8;
        this.previousPeriodAmSeries.tooltip.background.fillOpacity = 1;
        this.previousPeriodAmSeries.tooltip.keepTargetHover = true;
        this.previousPeriodAmSeries.tooltip.label.interactionsEnabled = true;
        // clear tooltip background shadow
        this.previousPeriodAmSeries.tooltip.background.filters.clear();
        this.previousPeriodAmSeries.tooltip.label.padding(0, 0, 0, 0);
        this.previousPeriodAmSeries.tooltip.label.fill = am4core.color(chartColors.agprBlack);
        // need to have this line, or else the tooltip won't display on hover over
        this.previousPeriodColumnTemplate.tooltipHTML = '';
        this.previousPeriodColumnTemplate.tooltipX = am4core.percent(this.options.styles?.tooltipX || 50);
        this.previousPeriodAmSeries.tooltip.pointerOrientation = 'horizontal';
        this.previousPeriodAmSeries.tooltip.boundingContainer = this.amchart;
        // set text to use as aria-label for each previous period column
        this.previousPeriodAmSeries.itemReaderText = this.amseries.itemReaderText;

        this.amseries.clustered = false;
        this.amseries.columns.template.tooltipX = am4core.percent(this.options.styles?.tooltipX || 50);
        this.amseries.tooltip.label.interactionsEnabled = true;
        this.amseries.tooltip.pointerOrientation = 'horizontal';
        this.amseries.tooltip.boundingContainer = this.amchart;

        if (!this.options.hidePreviousPeriod) {
            this.amchart.series.push(this.previousPeriodAmSeries);
        }

        // Add legend labels
        this.amchart.series.values.forEach(
            (amSeries, index) => {
                if (index === 0) {
                    amSeries.name = this.series?.[index]?.label;
                    amSeries.fill = am4core.color(this.styles.colorMap['barColor']);
                    amSeries.zIndex = 100;
                } else {
                    amSeries.name = this.options.previousPeriodLabel;
                    if (this.styles.colorMap['previousPeriodBarColor']) {
                        amSeries.fill = am4core.color(this.styles.colorMap['previousPeriodBarColor']);
                    } else {
                        amSeries.fill = am4core.color(this.styles.colorMap['previousBarColor']).lighten(0.8);
                    }
                    amSeries.strokeWidth = 1;
                    amSeries.strokeOpacity = 1;
                    amSeries.strokeDasharray = '3,2';
                    amSeries.stroke = am4core.color(this.styles.colorMap['barColor']);
                }
            }
        );

        if (this.options.currentPeriodLabel) {
            this.amseries.readerTitle = this.options.currentPeriodLabel;
        }
        if (!this.options.hideLegend) {
            this.amchart.legend = new Legend();
            this.amchart.legend.position = 'bottom';
            this.amchart.legend.markers.template.scale = 0.75;
            this.amchart.legend.itemContainers.template.paddingTop = 2;
            this.amchart.legend.itemContainers.template.paddingBottom = 2;
            this.amchart.legend.fontSize = '0.75rem';

            this.amchart.legend.labels.template.truncate = true;


            // this line for some reason fixes a style issue in the pdf export
            // but doesn't work when added to setExportStyles
            this.amchart.legend.strokeOpacity = 0;

            this.amchart.legend.dragWhileResize = true;
        }

        if (this.options.trendIndicators) {
            const uptrendColor = this.styles.colorMap?.['uptrendColor'];
            const downtrendColor = this.styles.colorMap?.['downtrendColor'];
            // Set series labels with arrows and color
            this.valueLabel.label.adapter.add(
                'text', (a, b, c) => {
                    const dataContext: DataPointModel = b.dataItem.dataContext as DataPointModel;
                    const difference: number = (+dataContext.xValue - +dataContext['xValuePreviousPeriod']);
                    const percentDifference: string = (Math.abs(difference / +dataContext['xValuePreviousPeriod']) * 100).toFixed(2);
                    let text = '';
                    if (difference > 0 && percentDifference !== '' + Infinity) {
                        text =  `[font-size:0.875rem ${uptrendColor ? uptrendColor : chartColors.agprPositive}]↑ ${percentDifference}%[/]`;
                    } else if (difference < 0 && percentDifference !== '' + Infinity) {
                        text =  `[font-size:0.875rem ${downtrendColor ? downtrendColor : chartColors.agprNegative}]↓ ${percentDifference}%[/]`;
                    } else {
                        text = '';
                    }

                    return text;
                }
            );
        }

        if (this.options.imageAsCategory) {
            this.columnTemplate.paddingLeft = 30;
            this.previousPeriodColumnTemplate.paddingLeft = 30;
            // this.valueLabel.dx = 30;
            this.valueLabel.paddingLeft = 35;
            this.categoryAxis.renderer.line.strokeOpacity = 1;
            this.categoryAxis.renderer.line.strokeWidth = 2;
            this.categoryAxis.renderer.line.stroke = am4core.color(chartColors.agprGreyBorder);
            this.categoryAxis.renderer.labels.template.disabled = true;
            this.categoryAxis.paddingLeft = 30;
            // Help hide a little of the first series beginning
            this.categoryAxis.dx = 1;

            this.valueAxis.paddingLeft = 30;
            this.valueAxis.renderer.grid.template.paddingLeft = 30;

            const image = new am4core.Image();
            image.width = 20;
            image.height = 20;
            image.verticalCenter = 'middle';
            image.dx = -32;
            image.dy = -4;
            image.tooltip = new am4core.Tooltip();
            image.tooltip.getFillFromObject = false;
            image.tooltip.pointerOrientation = 'left';
            image.tooltip.background.fill = am4core.color('#ffffff');
            image.tooltip.background.stroke = am4core.color('#ced4da');
            image.tooltip.background.cornerRadius = 8;
            image.tooltip.background.fillOpacity = 1;
            image.tooltip.background.filters.clear();
            image.tooltip.label.fill = am4core.color(chartColors.agprBlack);
            image.tooltip.pointerOrientation = 'left';
            image.tooltip.label.interactionsEnabled = true;
            image.tooltip.keepTargetHover = true;

            image.adapter.add('href', (href, target) => {
                const category = (target.dataItem as CategoryAxisDataItem).category;

                if (category) {
                    let text;
                    if (this.options.categoryLabelMap) {
                        text = this.options.categoryLabelMap.get(category);
                    }
                    target.tooltipHTML = `
                        <div style="font-size: 0.875rem; color: #292929; line-height: 1.5;">
                            ${text ? text : category}
                        </div>
                    `;
                    return this.options.appBaseHref + 'assets/images/' + category.toLowerCase() + '_logo.png';
                }

                return href;
            });
            this.categoryAxis.dataItems.template.bullet = image;

            if (this.options.responsive) {
                this.responsiveCategoryAxis.renderer.labels.template.disabled = true;
            }
        }

        if (this.options.responsive) {
            this.amchart.svgContainer.htmlElement.style.minHeight = '450px';
        }

        if (!this.options.responsive && !this.options.isSocialStyles) {
            const smallChartWidth = ChartSizeHelper.getWidthAtMinCanvas(3, 1);
            const smallChartHeight = ChartSizeHelper.getCellHeight();

            // Remove BarChartModel resize listener bc we need to handle a couple of things differently.
            if (this.amchart.events.hasListenersByType('sizechanged')) {
                this.amchart.events.off('sizechanged');
            }
            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;
                if (chart.pixelHeight && (chart.pixelHeight <= smallChartWidth || chart.pixelWidth <= smallChartHeight)) {
                    if (this.header) {
                        this.header.marginBottom = 0;
                    }

                    this.valueAxis.renderer.line.disabled = true;
                    this.valueAxis.renderer.grid.template.disabled = true;
                    this.valueAxis.renderer.labels.template.disabled = true;
                } else {
                    if (this.header) {
                        this.header.marginBottom = 10;
                    }

                    this.valueAxis.renderer.line.disabled = false;
                    this.valueAxis.renderer.grid.template.disabled = false;
                    this.valueAxis.renderer.labels.template.disabled = false;
                }
                if (this.titleLabel) {
                    if (chart.pixelWidth <= smallChartWidth) {
                        this.titleLabel.tooltipText = this.metadata.titleText;
                        this.titleLabel.tooltip.getFillFromObject = false;
                        this.titleLabel.tooltip.label.fill = am4core.color(chartColors.agprText);
                        this.titleLabel.tooltip.background.fill = am4core.color(chartColors.agprWhite);
                        this.titleLabel.tooltip.background.strokeOpacity = 1;
                        this.titleLabel.tooltip.background.fillOpacity = 1;
                        this.titleLabel.tooltip.background.cornerRadius = 3;
                        this.titleLabel.tooltip.dy = 10;
                    } else {
                        this.titleLabel.tooltipText = '';
                    }
                }


                if (this.rawData?.length) {
                    const flatData = this.getFlatData(this.rawData);
                    if (chart.pixelHeight && (chart.pixelHeight <= smallChartHeight && !this.isForSharedReport)) {
                        this.amchart.data = flatData.slice(0, this.smallChartDisplayLimit).reverse();
                    } else {
                        this.amchart.data = flatData.slice(0, this.chartDisplayLimit).reverse();
                    }

                    // Calulations to set the column labels above the column
                    if (this.metadata.type === ReportBlockTypeEnum.COVERAGE_COMPARISON) {
                        const chartHeight = this.amchart.chartContainer.pixelHeight;
                        const numOfColumns = this.amchart.data.length;
                        let axisHeight;

                        // When the chart is small we remove the axis labels so the total height of the axis changes
                        if (this.amchart.pixelWidth < smallChartWidth || chartHeight < smallChartHeight) {
                            axisHeight = 20;
                        } else {
                            axisHeight = 62;
                        }

                        // We set the column width to 60% of the cell width above
                        const colWidth = 0.6;

                        const actualWidth = ((chartHeight - axisHeight) / numOfColumns * colWidth);
                        const labelOffset = -(actualWidth / 2) - 5;

                        this.labelTemplate.dy = labelOffset;
                    }
                }
            });
        }
    }

    /**
     * Updates the current presentation of data in the chart
     * Expects 2 series, first series is current period, second is previous period
     */
    public updateData(data: DataPointModel[][], previousPeriod?: boolean): void {
        if (previousPeriod) {
            this.previousPeriodRawData = data;
        } else {
            this.rawData = data;
        }

        if (!data && !previousPeriod) {
            this.amchart.data = null;
            return;
        }

        this.mergeData();
        const flatData = this.getFlatData(this.rawData);

        if (!flatData.length && this.isContentEmpty()) {
            this.showNoContentMessage();
            this.amchart?.legend?.hide(0);
            this.noContentLabel.dom.style.display = 'block';
            this.valueAxis.hide(0);
            this.categoryAxis.hide(0);
            return;
        } else {
            if (this.noContentLabel) {
                this.noContentLabel.dom.style.display = 'none';
            }
        }

        this.amchart?.legend?.show(0);
        this.valueAxis.show(0);
        this.categoryAxis.show(0);
        if (this.noContentLabel) {
            this.noContentLabel.text = '';
        }
        if (this.options.barDisplayLimit) {
            this.chartDisplayLimit = this.options.barDisplayLimit;
        }

        this.dataIds = [];
        flatData.forEach(datum => {
            if (datum) {
                this.dataIds.push(datum.id);
            }
        });

        // the bar chart can only display a maximum of 10 bars. slice the first ten out.
        // the bars are placed from the bottom-up, not top-down, so the first element in the
        // dataset would appear on the bottom. this is probably less intuitive, so we reverse
        // the array to put the first element on the top of the chart.
        const smallChartHeight = ChartSizeHelper.getCellHeight();
        if (this.amchart.pixelHeight && (this.amchart.pixelHeight < smallChartHeight && !this.isForSharedReport)) {
            this.amchart.data = flatData.slice(0, this.smallChartDisplayLimit).reverse();
        } else {
            this.amchart.data = flatData.slice(0, this.chartDisplayLimit).reverse();
        }

        this.updateSeriesStyles();

        //  Calculate sum of xValue (current period + previous period)
        let xValueSum = 0;
        this.amchart.data.forEach(
            (chartData) => {
                if (chartData.xValue < 0.01 && this.options.percentLabel){
                    this.valueAxis.numberFormatter.numberFormat = '#.######%';
                }
                const xValue = chartData['xValue'] ? chartData['xValue'] : 0;
                const xValuePreviousPeriod = chartData['xValuePreviousPeriod'] ? chartData['xValuePreviousPeriod'] : 0;
                xValueSum += (xValue + xValuePreviousPeriod);
            }
        );
        // Set Tooltip HTML
        this.amchart.series.values.forEach(
            (amSeries, index) => {
                (amSeries as ColumnSeries).columns.template.adapter.add('tooltipHTML', (text, target) => {
                    let tooltipHeader = '{categoryY}';
                    if (this.options.categoryLabelMap) {
                        tooltipHeader = this.options.categoryLabelMap.get(target.dataItem.dataContext['id']);
                    }
                    let tooltip =
                        `<div
                            style="margin: 10px; font-size: 0.875rem;"
                        >
                            <div
                                style="border-bottom: 1px solid #ced4da; padding-bottom: 5px"
                            >
                                <b style="font-weight: 600">${tooltipHeader}</b>
                            </div>
                        `;
                    this.amchart.series.values.forEach((item, seriesIndex) => {
                        if (target.dataItem.dataContext['label'] !== 'noData' && (this.options as CategoryValueXYChartOptions).tooltipHTML) {
                            const viewMentionUrl = seriesIndex === 0 ?
                                target.dataItem.dataContext['viewMentionUrl'] : target.dataItem.dataContext['viewMentionUrlPreviousPeriod'];
                            const hasDataForPeriod = !!viewMentionUrl;

                            let xValue = seriesIndex === 0 ? +target.dataItem.dataContext['xValue'] : +target.dataItem.dataContext['xValuePreviousPeriod'];
                            if (!xValue) {
                                xValue = 0;
                            }
                            const totalPercent = ((xValue * 100) / xValueSum).toFixed(2);

                            tooltip += (this.options as CategoryValueXYChartOptions).tooltipHTML
                                .replace('{legendLabel}', item.name)
                                .replace('{totalPercent}', totalPercent)
                                .replace('{color}', seriesIndex === 0 ?  this.styles.colorMap['barColor'] : this.styles.colorMap['previousPeriodBarColor'])
                                .replace('{icon}',  seriesIndex === 0 ? 'fa-solid fa-square' : 'fa-solid fa-square-dashed')
                                .replace('{viewMentionUrl}',  viewMentionUrl)
                                .replace(/\{hasDataForPeriod\}/g, hasDataForPeriod ? 'visible' : 'hidden');
                            if (hasDataForPeriod) {
                                tooltip = tooltip.replace(/value/g, item.dataFields.valueX);
                            } else {
                                tooltip = tooltip.replace('{value}', 'N/A');
                            }
                        }
                    });

                    // Tooltip for shared report
                    if (target.dataItem.dataContext['label'] !== 'noData' && this.options.responsive) {
                        tooltip = tooltip.replace('{categoryY}', target.dataItem.dataContext['label']);

                        const hasPreviousPeriod = !this.options.hidePreviousPeriod;
                        const previousValue = target.dataItem.dataContext['xValuePreviousPeriod'];

                        tooltip += `
                            <div style="margin-top: 10px; display: flex; align-items: center">
                                <p style="margin: 0; word-break: break-word;">
                                    ${hasPreviousPeriod ? ('<b style="font-weight: 600;">' + this.options.currentPeriodLabel + ':</b>') : ''}
                                    ${abbreviateNumber(target.dataItem.dataContext['xValue'])}
                                </p>
                            </div>
                            ${hasPreviousPeriod ? `
                                <div style="margin-top: 10px; display: flex; align-items: center">
                                <p style="margin: 0; word-break: break-word;">
                                    <b style="font-weight: 600;"> ${this.options.previousPeriodLabel}:</b>
                                    ${ !previousValue && previousValue !== 0 ? 'N/A' : abbreviateNumber(previousValue, { decimals: 2 }) }
                                </p>
                                </div>` : ''}
                        </div>
                        `;
                    }

                    tooltip += '</div>';

                    return tooltip;
                });
            }
        );
        this.ready.next(true);
    }

    /**
     * merge previous data to raw data
     */
    mergeData(): void {
        if (this.previousPeriodRawData && this.rawData?.length) {
            const previousFlatData = this.getFlatData(this.previousPeriodRawData);
            const rawFlatData = this.getFlatData(this.rawData);

            rawFlatData.forEach(rawDataItem => {
                previousFlatData.forEach(previousDataItem => {
                    if (previousDataItem.label === rawDataItem.label) {
                        rawDataItem['xValuePreviousPeriod'] = previousDataItem['xValue'];
                        rawDataItem['previousBarColor'] = this.styles.colorMap?.['previousBarColor'];
                    }
                });

                if (!rawDataItem['xValuePreviousPeriod']) {
                    rawDataItem['xValuePreviousPeriod'] = 0;
                }
            });
        }
    }

    /**
     * get flat data
     */
    getFlatData(data: DataPointModel[][]): DataPointModel[] {
        const res = [];
        data?.forEach(dataArray => {
            dataArray?.forEach(item => {
                if (item) {
                    res.push(item);
                }
            });
        });

        return res;
    }

    clone(): IconBarChartModel {
        return new IconBarChartModel(
            this.series.map(s => s.clone()),
            this.options,
            this.metadata
        );
    }

    public updateSeriesStyles(): void {
        const colorMap = this.styles.colorMap;
        if (colorMap && Object.keys(colorMap).length > 0 && !colorMap['barColor']) {
            // when the chart has multi series colors
            this.dataIds.forEach((id, index) => {
                if (colorMap['seriesColor' + index]) {
                    this.colorList.set(id, colorMap['seriesColor' + index]);
                } else {
                    this.colorList.set(id,  colorMap['seriesColor0']);
                }
            });

            // when the chart has single series and multiple columns,
            // each column has its own color settings (eg: Social Coverage By Network)
            if (this.series?.length === 1) {
                this.amchart.data.forEach(data => {
                    if (colorMap[data.id]) {
                        this.colorList.set(data.id,  colorMap[data.id]);
                    }
                });
            }
            const colors =  this.colorList;
            this.amseries.columns.template.adapter.add('fill', function(fill, target) {
                if (target.dataItem.dataContext['id']) {
                    target.stroke = am4core.color(colors.get(target.dataItem.dataContext['id']));
                    return am4core.color(colors.get(target.dataItem.dataContext['id']));
                }
            });
        }
    }

    /**
     * Exports a chart via amcharts
     */
    public async export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {
        const dataFields: any = {
            label: this.options.label,
            xValue: this.options.xValueLabel
        };

        if (this?.styles?.chartDisplaySettings?.previousPeriod) {
            dataFields.xValuePreviousPeriod = this.options.xValueLabel + ' - ' + this.options.previousPeriodLabel;
        }

        if (this.options.percentLabel) {
            dataFields.percent = this.options.percentLabel;
        }

        this.amchart.exporting.dataFields = dataFields;
        this.amchart.exporting.filePrefix = filename;
        this.amchart.exporting.useWebFonts = false;

        return await this.amchart.exporting.export(type);
    }
}
